import * as React from "react"
import { ArrowDownIcon, ArrowUpIcon, ExternalLinkIcon } from "lucide-react"

import {
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Skeleton,
  cn,
} from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import {
  FinanceHealthRatioUnit,
  FinanceHealthStatus,
  type FinanceHealthRatio,
  type FinanceHealthRatioId,
} from "@ibada/shared-types/finance"

import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import { OverviewRoutes, useOverviewNav } from "@/features/finance/overview/lib/overview-nav"
import { FINANCIAL_RATIO_REPORT_ROUTES } from "@/features/finance/overview/lib/financial-health-registry"
import { useFinancialHealthData } from "@/features/finance/overview/lib/use-financial-health-data"

type Translate = ReturnType<typeof useTranslations>

const HEALTH_DOT: Record<FinanceHealthStatus, string> = {
  [FinanceHealthStatus.Good]: "bg-emerald-500",
  [FinanceHealthStatus.Neutral]: "bg-emerald-500",
  [FinanceHealthStatus.Warning]: "bg-amber-500",
  [FinanceHealthStatus.Critical]: "bg-rose-500",
}

function formatValue(ev: FinanceHealthRatio, value: number | null): string {
  if (value == null) return "—"
  return ev.unit === FinanceHealthRatioUnit.Percent ? `${Math.round(value)}%` : value.toFixed(2)
}

function formatDelta(ev: FinanceHealthRatio, delta: number): string {
  return ev.unit === FinanceHealthRatioUnit.Percent
    ? `${Math.abs(Math.round(delta))} pts`
    : Math.abs(delta).toFixed(2)
}

function healthLabel(t: Translate, ev: FinanceHealthRatio): string {
  if (!ev.available) return t("widgets.financialHealth.unavailable")
  if (ev.value == null) return t("widgets.financialHealth.noLiabilities")
  return t(`widgets.financialHealth.health.${ev.status}`)
}

/**
 * A single financial-health ratio as its own dashboard widget (draggable/hideable independently). Its title
 * (the ratio name) comes from i18n; the body shows the value + verdict + trend vs the previous equal-length
 * period + a one-line meaning, and opens a details drawer.
 *
 * The ratio, its variance and its verdict are NOT computed here (IBA-117) — they arrive from
 * `GET /finance/overview/health`, derived from the ledger. Data is shared across all ratio widgets via React
 * Query dedup, but each keeps its own boundary/refresh.
 */
export function RatioWidget({ id }: { id: FinanceHealthRatioId }) {
  const t = useTranslations("finance.overview")
  const go = useOverviewNav()
  const health = useFinancialHealthData()
  const [detailsOpen, setDetailsOpen] = React.useState(false)

  const ev: FinanceHealthRatio | undefined = health.ratio(id)

  useWidgetMenu({
    refresh: () => health.refetch(),
    viewDetails: () => setDetailsOpen(true),
  })

  return (
    <>
      <WidgetBoundary
        isLoading={health.isLoading}
        isError={health.isError}
        isFetching={health.isFetching}
        data={ev}
        isEmpty={(value) => !value.available}
        skeleton={<RatioSkeleton />}
        errorTitle={t("widgets.financialHealth.error")}
        onRetry={() => health.refetch()}
        retryLabel={t("widgets.common.retry")}
        emptyTitle={t("widgets.financialHealth.empty.title")}
        emptyDescription={t("widgets.financialHealth.empty.description")}
      >
        {(value) => <RatioBody t={t} ev={value} onOpen={() => setDetailsOpen(true)} />}
      </WidgetBoundary>

      <RatioDetailsDrawer
        t={t}
        ev={detailsOpen ? ev ?? null : null}
        onClose={() => setDetailsOpen(false)}
        // Undefined where there is nowhere honest to send the reader — the drawer drops the button rather
        // than showing one that does nothing.
        onViewReport={go.canNavigateTo(OverviewRoutes.investigations) ? go : undefined}
      />
    </>
  )
}

function RatioBody({ t, ev, onOpen }: { t: Translate; ev: FinanceHealthRatio; onOpen: () => void }) {
  // The variance and its direction are the server's, not the browser's.
  const delta = ev.delta
  const improving = ev.improving ?? false
  return (
    <button type="button" onClick={onOpen} className="group flex w-full flex-col gap-1.5 text-left">
      <div className="flex items-baseline gap-2">
        <span
          className="text-2xl font-semibold tabular-nums leading-none tracking-tight"
        >
          {formatValue(ev, ev.value)}
        </span>
        <span className="flex items-center gap-1 text-xs">
          <span className={cn("size-1.5 rounded-full", HEALTH_DOT[ev.status])} aria-hidden="true" />
          <span className="text-muted-foreground">{healthLabel(t, ev)}</span>
        </span>
      </div>
      {delta != null && delta !== 0 ? (
        <span
          className={cn(
            "flex items-center gap-0.5 text-[11px] tabular-nums",
            improving ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400",
          )}
        >
          {delta > 0 ? <ArrowUpIcon className="size-3" /> : <ArrowDownIcon className="size-3" />}
          {formatDelta(ev, delta)}
          <span className="text-muted-foreground">{t("widgets.financialHealth.vsPrevious")}</span>
        </span>
      ) : null}
      <span className="text-[11px] leading-snug text-muted-foreground">
        {t(`widgets.financialHealth.ratios.${ev.id}.explanation`)}
      </span>
    </button>
  )
}

function RatioDetailsDrawer({
  t,
  ev,
  onClose,
  onViewReport,
}: {
  t: Translate
  ev: FinanceHealthRatio | null
  onClose: () => void
  onViewReport?: (route: string) => void
}) {
  const delta = ev?.delta ?? null
  return (
    <Sheet open={ev != null} onOpenChange={(next) => (!next ? onClose() : undefined)} mobileBottomSheet>
      <SheetContent side="right" className="flex flex-col gap-0">
        {ev ? (
          <>
            <SheetHeader>
              <SheetTitle>{t(`widgets.financialHealth.ratios.${ev.id}.name`)}</SheetTitle>
              <SheetDescription>{t(`widgets.financialHealth.ratios.${ev.id}.explanation`)}</SheetDescription>
            </SheetHeader>
            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3 text-sm">
              <DetailRow label={t("widgets.financialHealth.details.current")} value={formatValue(ev, ev.value)} />
              <DetailRow
                label={t("widgets.financialHealth.details.previous")}
                value={formatValue(ev, ev.previous)}
              />
              <DetailRow
                label={t("widgets.financialHealth.details.trend")}
                value={delta != null && delta !== 0 ? `${delta > 0 ? "↑" : "↓"} ${formatDelta(ev, delta)}` : "—"}
              />
              <DetailRow label={t("widgets.financialHealth.details.status")} value={healthLabel(t, ev)} />
              <div className="space-y-1 border-t pt-3">
                <p className="text-xs font-medium text-muted-foreground">
                  {t("widgets.financialHealth.details.formula")}
                </p>
                <p className="rounded-md bg-muted px-2 py-1.5 text-xs">
                  {t(`widgets.financialHealth.ratios.${ev.id}.formula`)}
                </p>
              </div>
            </div>
            <SheetFooter>
              {onViewReport ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    onViewReport(FINANCIAL_RATIO_REPORT_ROUTES[ev.id])
                    onClose()
                  }}
                >
                  <ExternalLinkIcon className="size-4" />
                  {t("widgets.financialHealth.details.viewReport")}
                </Button>
              ) : null}
            </SheetFooter>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  )
}

function RatioSkeleton() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-7 w-20" />
      <Skeleton className="h-3 w-16" />
      <Skeleton className="h-3 w-full" />
    </div>
  )
}
